import Phaser from 'phaser';
import { TILE_SIZE, MAP_WIDTH, MAP_HEIGHT, FARM_SIZE } from '../data/world-config';

// Tile index constants matching the terrain spritesheet layout
// Row 0: Grass 0-3, Dirt 4-7
// Row 1: Water 8-11, Sand 12-15
// Row 2: Stone path 16-19, Flowers 20-23
// Row 3: Farm soil 24-27, Fence 28-31
// Row 4: Dark forest 32-35, Marsh 36-39
// Row 5: Rocky ground 40-43, Meadow 44-47
const TILE = {
  GRASS: 0,        // 0-3
  DIRT: 4,         // 4-7
  WATER: 8,        // 8-11
  SAND: 12,        // 12-15
  STONE_PATH: 16,  // 16-19
  FLOWER: 20,      // 20-23
  DARK_FOREST: 32, // 32-35
  MARSH: 36,       // 36-39
  ROCKY: 40,       // 40-43
  MEADOW: 44,      // 44-47
};

export class WorldGenerator {
  private scene: Phaser.Scene;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  generate(): { map: Phaser.Tilemaps.Tilemap; terrainLayer: Phaser.Tilemaps.TilemapLayer } {
    const map = this.scene.make.tilemap({
      tileWidth: TILE_SIZE,
      tileHeight: TILE_SIZE,
      width: MAP_WIDTH,
      height: MAP_HEIGHT,
    });

    const tileset = map.addTilesetImage('terrain', 'terrain', TILE_SIZE, TILE_SIZE, 0, 0)!;
    const terrainLayer = map.createBlankLayer('terrain', tileset, 0, 0)!;

    this.generateTerrain(terrainLayer);

    // Water tiles are collidable
    terrainLayer.setCollisionBetween(8, 11);

    return { map, terrainLayer };
  }

  private generateTerrain(layer: Phaser.Tilemaps.TilemapLayer): void {
    const cx = Math.floor(MAP_WIDTH / 2);
    const cy = Math.floor(MAP_HEIGHT / 2);

    // Pre-compute biome map using multi-octave noise
    for (let y = 0; y < MAP_HEIGHT; y++) {
      for (let x = 0; x < MAP_WIDTH; x++) {
        const tileIndex = this.getTileForPosition(x, y, cx, cy);
        layer.putTileAt(tileIndex, x, y);
      }
    }

    // Place decorative tree sprites (objects layer) via events
    this.placeTreeClusters(cx, cy);
  }

  private getTileForPosition(x: number, y: number, cx: number, cy: number): number {
    const dx = x - cx;
    const dy = y - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // === Farm area (center) ===
    const farmHalf = FARM_SIZE / 2;
    if (Math.abs(dx) <= farmHalf + 2 && Math.abs(dy) <= farmHalf + 2) {
      if (Math.abs(dx) <= farmHalf && Math.abs(dy) <= farmHalf) {
        return this.seededRandom(x, y) > 0.5 ? 0 : 4;
      }
      return this.seededRandom(x, y) > 0.3 ? 0 : 1;
    }

    // === World edges — beach/sand ===
    const edgeDist = Math.min(x, y, MAP_WIDTH - 1 - x, MAP_HEIGHT - 1 - y);
    if (edgeDist < 4) {
      return TILE.SAND + this.irand(x, y, 4);
    }
    if (edgeDist < 7) {
      // Beach transition — mix sand and grass
      return this.seededRandom(x * 5, y * 7) > 0.5
        ? TILE.SAND + this.irand(x, y, 4)
        : this.irand(x, y, 4); // grass
    }

    // === Rivers ===
    // Main river — runs roughly N-S with meander through western third
    const riverMainX = cx - 30 + Math.floor(Math.sin(y * 0.04) * 12 + Math.sin(y * 0.11) * 5);
    if (Math.abs(x - riverMainX) <= 2) {
      return TILE.WATER + (x + y) % 4;
    }
    // Sandy riverbank
    if (Math.abs(x - riverMainX) <= 4) {
      return this.seededRandom(x, y) > 0.4 ? TILE.SAND + this.irand(x, y, 4) : this.irand(x, y, 4);
    }

    // Tributary — runs E-W through southern area
    const riverTribY = cy + 40 + Math.floor(Math.sin(x * 0.06) * 8);
    if (Math.abs(y - riverTribY) <= 1 && x > riverMainX + 4 && x < MAP_WIDTH - 15) {
      return TILE.WATER + (x + y) % 4;
    }

    // === Ponds / Lakes ===
    // Northeast lake
    const lake1CX = cx + 55, lake1CY = cy - 40;
    const lake1D = Math.sqrt((x - lake1CX) ** 2 + (y - lake1CY) ** 2);
    if (lake1D < 8) return TILE.WATER + (x + y) % 4;
    if (lake1D < 11) return TILE.SAND + this.irand(x, y, 4);

    // Southwest pond
    const pond1CX = cx - 50, pond1CY = cy + 50;
    const pond1D = Math.sqrt((x - pond1CX) ** 2 + (y - pond1CY) ** 2);
    if (pond1D < 5) return TILE.WATER + (x + y) % 4;
    if (pond1D < 7) return TILE.MARSH + this.irand(x, y, 4);

    // Small pond near farm
    const pond2CX = cx + 25, pond2CY = cy + 15;
    const pond2D = Math.sqrt((x - pond2CX) ** 2 + (y - pond2CY) ** 2);
    if (pond2D < 4) return TILE.WATER + (x + y) % 4;
    if (pond2D < 6) return TILE.SAND + this.irand(x, y, 4);

    // === Stone paths connecting areas ===
    // Main east-west road from farm
    if (Math.abs(y - cy) <= 1 && (x > cx + farmHalf + 3) && x < riverMainX - 4) {
      return TILE.STONE_PATH + (x % 4);
    }
    // Path south from farm
    if (Math.abs(x - cx) <= 1 && y > cy + farmHalf + 3 && y < riverTribY - 3) {
      return TILE.STONE_PATH + (y % 4);
    }
    // Path north from farm
    if (Math.abs(x - cx) <= 1 && y < cy - farmHalf - 3 && y > 15) {
      return TILE.STONE_PATH + (y % 4);
    }
    // Path east toward lake
    if (Math.abs(y - (cy - 40)) <= 1 && x > cx + 20 && x < lake1CX - 12) {
      return TILE.STONE_PATH + (x % 4);
    }

    // === Biome zones (using distance + angle from center) ===
    const angle = Math.atan2(dy, dx); // -PI to PI
    const normAngle = (angle + Math.PI) / (2 * Math.PI); // 0 to 1

    // Dark forest — northwest quadrant, away from farm
    if (dx < -20 && dy < -15 && dist > 30) {
      const forestNoise = this.noise2d(x * 0.08, y * 0.08);
      if (forestNoise > 0.3) {
        return TILE.DARK_FOREST + this.irand(x, y, 4);
      }
      // Forest edge — regular grass with some dark patches
      return this.seededRandom(x, y) > 0.7
        ? TILE.DARK_FOREST + this.irand(x, y, 4)
        : this.irand(x, y, 4);
    }

    // Marsh — southwest, near the river
    if (dx < -10 && dy > 25 && dist > 35) {
      const marshNoise = this.noise2d(x * 0.07, y * 0.09);
      if (marshNoise > 0.25) {
        return TILE.MARSH + this.irand(x, y, 4);
      }
      return this.irand(x, y, 4);
    }

    // Rocky highlands — northeast, past the lake
    if (dx > 30 && dy < -10 && dist > 45) {
      const rockyNoise = this.noise2d(x * 0.06, y * 0.06);
      if (rockyNoise > 0.2) {
        return TILE.ROCKY + this.irand(x, y, 4);
      }
      return TILE.DIRT + this.irand(x, y, 4);
    }

    // Meadow — southeast, gentle rolling fields
    if (dx > 15 && dy > 15 && dist > 25) {
      const meadowNoise = this.noise2d(x * 0.05, y * 0.05);
      if (meadowNoise > 0.2) {
        return TILE.MEADOW + this.irand(x, y, 4);
      }
      // Scattered flowers in meadow fringe
      if (this.seededRandom(x * 7, y * 13) > 0.85) {
        return TILE.FLOWER + this.irand(x, y, 4);
      }
    }

    // === Scattered features in grassland ===
    // Flower patches near farm
    if (dist > 15 && dist < 30) {
      const flowerNoise = this.seededRandom(x * 7, y * 13);
      if (flowerNoise > 0.88) {
        return TILE.FLOWER + Math.floor(flowerNoise * 100) % 4;
      }
    }

    // Dirt patches in various places
    if (dist > 20) {
      const dirtNoise = this.noise2d(x * 0.12, y * 0.12);
      if (dirtNoise > 0.65) {
        return TILE.DIRT + this.irand(x, y, 4);
      }
    }

    // Default: grass with variation
    const grassVar = this.seededRandom(x * 3, y * 5);
    return Math.floor(grassVar * 4);
  }

  /** Place tree object sprites in clusters (not on the tilemap). */
  private placeTreeClusters(cx: number, cy: number): void {
    const trees: { x: number; y: number; frame: number }[] = [];
    const farmHalf = FARM_SIZE / 2 + 4;

    // Dense tree clusters in the dark forest
    for (let y = 10; y < cy - 15; y += 3) {
      for (let x = 10; x < cx - 20; x += 3) {
        if (this.seededRandom(x * 11, y * 17) > 0.55) {
          trees.push({ x, y, frame: 12 }); // tree stump frame as placeholder
        }
      }
    }

    // Scattered orchard trees in meadow area
    for (let y = cy + 25; y < MAP_HEIGHT - 20; y += 6) {
      for (let x = cx + 25; x < MAP_WIDTH - 20; x += 6) {
        if (this.seededRandom(x * 13, y * 19) > 0.6) {
          trees.push({ x, y, frame: 12 });
        }
      }
    }

    // Random lone trees in grassland
    for (let y = 15; y < MAP_HEIGHT - 15; y += 8) {
      for (let x = 15; x < MAP_WIDTH - 15; x += 8) {
        const dx = x - cx;
        const dy = y - cy;
        if (Math.abs(dx) < farmHalf && Math.abs(dy) < farmHalf) continue;
        if (this.seededRandom(x * 23, y * 29) > 0.75) {
          trees.push({ x, y, frame: 12 });
        }
      }
    }

    // Create tree sprites
    for (const tree of trees) {
      const sprite = this.scene.add.sprite(
        tree.x * TILE_SIZE + TILE_SIZE / 2,
        tree.y * TILE_SIZE + TILE_SIZE / 2,
        'objects',
        tree.frame
      );
      sprite.setDepth(4);
    }
  }

  /** Simple seeded pseudo-random for consistent terrain. */
  private seededRandom(x: number, y: number): number {
    let seed = x * 374761393 + y * 668265263;
    seed = (seed ^ (seed >> 13)) * 1274126177;
    seed = seed ^ (seed >> 16);
    return (seed & 0x7fffffff) / 0x7fffffff;
  }

  /** Integer variant 0..n-1 from position. */
  private irand(x: number, y: number, n: number): number {
    return Math.floor(this.seededRandom(x * 3, y * 5) * n);
  }

  /** Value-noise style 2D noise (smooth, 0-1 range). */
  private noise2d(x: number, y: number): number {
    const ix = Math.floor(x);
    const iy = Math.floor(y);
    const fx = x - ix;
    const fy = y - iy;

    const a = this.seededRandom(ix, iy);
    const b = this.seededRandom(ix + 1, iy);
    const c = this.seededRandom(ix, iy + 1);
    const d = this.seededRandom(ix + 1, iy + 1);

    // Smoothstep
    const sx = fx * fx * (3 - 2 * fx);
    const sy = fy * fy * (3 - 2 * fy);

    const top = a + (b - a) * sx;
    const bot = c + (d - c) * sx;
    return top + (bot - top) * sy;
  }
}
